import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, API, getToken } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Building2, Upload, Save, Image as ImageIcon, MapPin, Hash, Mail, Phone, FileText, Percent, Bell, Rocket } from "lucide-react";
import { PushSettingsToggle, SnoozeSettings } from "@/components/PushNotifications";
import { NotificationSettingsPanel } from "@/components/NotificationSettingsPanel";
import { ChefMetiersCard } from "@/components/chef/ChefMetiersCard";

export default function ChefProfilEntreprise() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/chef/entreprise/profile");
      setProfile(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const update = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        adresse: profile.adresse || "",
        ville: profile.ville || "",
        code_postal: profile.code_postal || "",
        siret: profile.siret || "",
        ape: profile.ape || "",
        email: profile.email || "",
        telephone: profile.telephone || "",
        iban: profile.iban || "",
        convention_collective: profile.convention_collective || "",
        urssaf: profile.urssaf || "",
        taux_cotisations: profile.taux_cotisations != null && profile.taux_cotisations !== "" ? parseFloat(profile.taux_cotisations) : null,
      };
      const { data } = await api.patch("/chef/entreprise/profile", payload);
      setProfile(data);
      toast.success("Profil entreprise enregistré");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post("/chef/entreprise/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo téléchargé");
      setLogoVersion((v) => v + 1);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!profile) return <div className="p-10 text-sm">Chargement…</div>;

  const logoUrl = profile.logo_path
    ? `${API}/files/logo/${profile.id}?v=${logoVersion}`
    : null;

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Entreprise"
        title="Profil entreprise"
        sub="Renseignez les informations qui apparaîtront sur les bulletins de paie générés."
        right={
          <Button onClick={save} disabled={saving} data-testid="profile-save" className="h-11 bg-[#002FA7] hover:bg-[#002175] text-white font-semibold">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        }
      />
      <div className="p-6 lg:p-10 grid lg:grid-cols-3 gap-6 max-w-6xl">
        {profile.en_projet && (
          <div className="lg:col-span-3 border-2 border-[#FFD700] bg-[#FFFBEB] p-4 flex flex-wrap items-start gap-3" data-testid="profile-projet-banner">
            <Rocket className="h-5 w-5 text-[#92400E] shrink-0 mt-0.5" />
            <div className="text-sm text-[#92400E] flex-1 min-w-[240px]">
              <div className="font-display font-bold">Entreprise en projet</div>
              <p className="text-xs mt-0.5">Vous vous êtes inscrit(e) sans SIRET. Dès l'immatriculation, renseignez votre <b>SIRET</b> ci-dessous et enregistrez : votre compte passe en « immatriculée » et votre extrait KBIS vous sera alors demandé.</p>
            </div>
          </div>
        )}
        {/* Logo + identity */}
        <div className="lg:col-span-1 space-y-6">
          <Card icon={ImageIcon} title="Logo">
            <div className="aspect-square bg-[#F8F9FA] border border-[#DEE2E6] flex items-center justify-center overflow-hidden">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo entreprise" className="max-w-full max-h-full object-contain" data-testid="entreprise-logo-preview" />
              ) : (
                <div className="text-center text-[#868E96] text-sm">
                  <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-40" />
                  <div>Aucun logo</div>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={uploadLogo} className="hidden" data-testid="logo-input" />
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              variant="outline"
              data-testid="upload-logo-btn"
              className="w-full"
            >
              <Upload className="h-4 w-4 mr-2" /> {logoUrl ? "Changer le logo" : "Téléverser un logo"}
            </Button>
            <div className="text-[10px] text-[#868E96]">PNG, JPG, WEBP ou SVG · 2 Mo max.</div>
          </Card>

          <Card icon={Building2} title="Identité">
            <F label="Raison sociale" value={profile.name || ""} disabled />
            <F label="Code entreprise" value={profile.code || ""} disabled mono />
          </Card>
        </div>

        {/* Address + Legal */}
        <div className="lg:col-span-2 space-y-6">
          <Card icon={MapPin} title="Adresse">
            <F label="Adresse" value={profile.adresse} onChange={(v) => update("adresse", v)} testid="adresse" />
            <div className="grid grid-cols-3 gap-3">
              <F label="Code postal" value={profile.code_postal} onChange={(v) => update("code_postal", v)} testid="cp" />
              <div className="col-span-2"><F label="Ville" value={profile.ville} onChange={(v) => update("ville", v)} testid="ville" /></div>
            </div>
          </Card>

          <Card icon={Hash} title="Informations légales">
            <div className="grid grid-cols-2 gap-3">
              <F label={profile.en_projet ? "SIRET (à renseigner dès l'immatriculation)" : "SIRET (obligatoire — 14 chiffres)"} value={profile.siret} onChange={(v) => update("siret", v)} testid="siret" />
              <F label="Code APE / NAF" value={profile.ape} onChange={(v) => update("ape", v)} testid="ape" />
            </div>
            <F label="Convention collective" value={profile.convention_collective} onChange={(v) => update("convention_collective", v)} testid="cc" />
            <F label="N° URSSAF" value={profile.urssaf} onChange={(v) => update("urssaf", v)} testid="urssaf" />
            <F label="IBAN (paiement salaires)" value={profile.iban} onChange={(v) => update("iban", v)} testid="iban" mono />
          </Card>

          <Card icon={Mail} title="Contact">
            <div className="grid grid-cols-2 gap-3">
              <F label="Email professionnel" type="email" value={profile.email} onChange={(v) => update("email", v)} testid="email" />
              <F label="Téléphone" type="tel" value={profile.telephone} onChange={(v) => update("telephone", v)} testid="telephone" />
            </div>
          </Card>

          <Card icon={Percent} title="Calcul du net à payer">
            <F
              label="Taux estimatif des cotisations salariales (%)"
              type="number"
              value={profile.taux_cotisations ?? ""}
              onChange={(v) => update("taux_cotisations", v)}
              testid="taux-cot"
            />
            <div className="text-xs text-[#495057]">
              Le net à payer affiché sur les bulletins simplifiés est calculé : <span className="font-bold">Brut − ({"taux"} × Brut)</span>.
              Valeur recommandée : <b>22%</b>. Vous pouvez aussi saisir le net manuellement à la génération.
            </div>
          </Card>

          <Card icon={Bell} title="Notifications push">
            <div className="text-xs text-[#495057] mb-2">
              Recevez les nouveaux messages, tickets et congés en <b>notifications instantanées</b> comme un SMS,
              même quand PAG est fermé (Chrome/Edge/Safari 16+, iOS 16.4+).
            </div>
            <PushSettingsToggle />
            <div className="mt-3">
              <SnoozeSettings />
            </div>
          </Card>

          <ChefMetiersCard />

          <NotificationSettingsPanel />
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-[#DEE2E6] p-6 space-y-3">
      <div className="flex items-center gap-3 mb-2">
        <Icon className="h-5 w-5 text-[#002FA7]" />
        <h3 className="font-display font-bold text-base tracking-tight">{title}</h3>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function F({ label, value, onChange, type = "text", testid, disabled, mono }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">{label}</Label>
      <Input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        disabled={disabled}
        className={`h-10 border-[#DEE2E6] focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 ${mono ? "font-mono text-sm" : ""}`}
      />
    </div>
  );
}
